<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { flashMessage } from '@/utils/FlashMessages'
import { serverService } from '@/core/services'
import Server from '@/core/resources/Server'
import { useStore } from '@/core/store'
import router from '@/router'
import { useRoute } from 'vue-router'
import { popupUtil } from '@/utils/Popup'
import { handleError } from '@/utils/ErrorHandler'
import TextInput from '@/components/input/TextInput.vue'

const route = useRoute()
const store = useStore()
const serverName = ref<string>('')
const hostname = ref<string>('')
const username = ref<string>('')
const password = ref<string>('')
const port = ref<number>(22)
const baseDirectory = ref<string>('/')

const id = ref(Number(route.params.id))

onMounted(() => {
  if (!id.value) return

  serverService
    .get(id.value, store.getAccessToken())
    .then((server) => {
      serverName.value = server.name
      hostname.value = server.hostname
      username.value = server.username
      password.value = server.password
      port.value = server.port
      baseDirectory.value = server.basedir
    })
    .catch(() => {
      router.push({ name: 'servers' })
    })
})

const createServer = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to create this server?')

  if (!confirmed) return

  const server = new Server()
  server.name = serverName.value
  server.hostname = hostname.value
  server.username = username.value
  server.password = password.value
  server.port = port.value
  server.basedir = baseDirectory.value

  serverService
    .create(server, store.getAccessToken())
    .then(() => {
      flashMessage.success('Server created successfully')
      router.push({ name: 'servers' })
    })
    .catch(handleError)
}

const editServer = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to edit this server?')

  if (!confirmed) return

  const server = new Server()
  server.id = id.value
  server.name = serverName.value
  server.hostname = hostname.value
  server.username = username.value
  server.password = password.value
  server.port = port.value
  server.basedir = baseDirectory.value.endsWith('/') && baseDirectory.value != '/' ? baseDirectory.value.slice(0, -1) : baseDirectory.value

  serverService
    .update(server, store.getAccessToken())
    .then(() => {
      flashMessage.success('Server edited successfully')
      router.push({ name: 'servers' })
    })
    .catch(handleError)
}
</script>

<template>
  <div class="container">
    <form novalidate>
      <h3 v-if="!id">Create a new server</h3>
      <h3 v-else>Edit server</h3>

      <div class="form-group">
        <TextInput
          v-model="serverName"
          :name="'servername'"
          :id="'servername'"
          :placeholder="'Server name'"
          :type="'text'"
          :required="true" />

        <TextInput v-model="hostname" :name="'hostname'" :id="'hostname'" :placeholder="'Hostname'" :type="'text'" :required="true" />

        <TextInput v-model="username" :name="'username'" :id="'username'" :placeholder="'Username'" :type="'text'" :required="true" />

        <TextInput v-model="password" :name="'password'" :id="'password'" :placeholder="'Password'" :type="'password'" :required="true" />

        <TextInput v-model="port" :name="'port'" :id="'port'" :placeholder="'Port'" :type="'text'" :required="true" />

        <TextInput
          v-model="baseDirectory"
          :name="'basedirectory'"
          :id="'basedirectory'"
          :placeholder="'Base directory'"
          :type="'text'"
          :required="true" />
      </div>

      <button v-if="!id" type="button" class="submit-button" @click="createServer">Create server</button>
      <button v-else type="button" class="submit-button" @click="editServer">Update server</button>
    </form>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/input.scss';
@import '@/assets/styles/form.scss';

form {
  width: clamp(350px, 70%, 900px);
}

form .form-group {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}
</style>
